import { Monster } from './Monster';
import { Earth } from '../earth/Earth';
import Phaser from 'phaser';
import { AudioManager, SoundEffect } from '../../systems/AudioManager';

export class FastAlien extends Monster {

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    this.health = 25;
    this.maxHealth = 25;
    this.speed = 60; // Twice as fast as BasicAlien
    this.reward = 15;
    this.specialAbility = 'Fast Movement';
    
    // Visual representation - smaller, faster monster
    const circle = scene.add.circle(0, 0, 6, 0xff6600);
    circle.setName('monsterCircle');
    this.add(circle);
  }

  override update(time: number, delta: number): void {
    super.update(time, delta);
    // Find Earth if not found
    if (!this.targetEarth) {
      this.findEarth();
    }
    
    // Move towards Earth
    if (this.targetEarth) {
      this.move();
    }
  }

  override move(): void {
    if (!this.targetEarth) return;
    
    // Calculate direction to Earth
    const direction = new Phaser.Math.Vector2(
      this.targetEarth.x - this.x,
      this.targetEarth.y - this.y
    ).normalize();
    
    // Move towards Earth
    this.x += direction.x * this.speed * 0.016; // 60 FPS
    this.y += direction.y * this.speed * 0.016;
    
    // Check if reached Earth
    const distance = Phaser.Math.Distance.Between(this.x, this.y, this.targetEarth.x, this.targetEarth.y);
    if (distance < 60) { // Earth radius + monster radius
      this.attackEarth();
    }
  }

  override attackEarth(): void {
    const _this = this;
    if (!this.targetEarth) return;
    
    // Create fast attack impact effect
    this.createFastAttackEffect();
    
    // Fast aliens deal less damage but attack faster
    const baseDamage = 8;
    if (this.isActive) this.targetEarth.takeDamage(baseDamage);
    
    // Play attack sound
    const audioManager = AudioManager.getInstance(this.scene);
    audioManager.playSFX(SoundEffect.EARTH_DAMAGE);
    
    // Mark as inactive before destroying
    this.isActive = false;
    
    // Fast death explosion
    this.createFastDeathExplosion();
    
    // Shorter delay for fast alien
    this.scene?.time.delayedCall(50, () => {
      _this.destroy();
    });
  }

  private createFastAttackEffect(): void {
    if (!this.scene) return;
    // Quick streak effect for fast impact
    const streak = this.scene?.add.rectangle(this.x, this.y, 20, 3, 0x44ff44, 0.9);
    streak.setRotation(Math.atan2(this.targetEarth!.y - this.y, this.targetEarth!.x - this.x));
    
    this.scene?.tweens.add({
      targets: streak,
      scaleX: 3,
      alpha: 0,
      duration: 200,
      ease: 'Power2',
      onComplete: () => streak.destroy()
    });
    
    // Fast impact sparks
    for (let i = 0; i < 8; i++) {
      const angle = (Math.PI * 2 * i) / 8;
      const spark = this.scene.add.circle(this.x, this.y, 1, 0x88ff88);
      
      this.scene?.tweens.add({
        targets: spark,
        x: this.x + Math.cos(angle) * 25,
        y: this.y + Math.sin(angle) * 25,
        alpha: 0,
        duration: 300,
        ease: 'Power2',
        onComplete: () => spark.destroy()
      });
    }
  }

  private createFastDeathExplosion(): void {
    // Quick green explosion for fast alien
    if (!this.scene) return;
    const explosion = this.scene?.add.circle(this.x, this.y, 6, 0x44ff44, 0.8);
    this.scene?.tweens.add({
      targets: explosion,
      scaleX: 2,
      scaleY: 2,
      alpha: 0,
      duration: 200,
      ease: 'Power2',
      onComplete: () => explosion.destroy()
    });
  }

  override activateAbility(): void {
    // Fast alien has fast movement as passive ability
  }

  override takeDamage(amount: number): void {
    super.takeDamage(amount);
    
    // Visual feedback - change color temporarily
    const originalColor = 0x00ffff;
    const flashColor = 0xffffff;
    
    // Find the circle visual and change its color
    const circle = this.getByName('monsterCircle');
    if (circle instanceof Phaser.GameObjects.Arc) {
      circle.setFillStyle(flashColor);
      
      // Restore original color after 100ms
      this.scene.time.delayedCall(100, () => {
        circle.setFillStyle(originalColor);
      });
    } else if (circle instanceof Phaser.GameObjects.Graphics) {
      circle.clear();
      circle.fillStyle(flashColor);
      this.scene?.time.delayedCall(100, () => {
        circle.fillStyle(originalColor);
      });
    }
  }
} 